import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const Checkout = ({ cart, total, onClose, onOrderComplete }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: currentUser?.email || '',
    customerPhone: '',
    shippingAddress: '',
    paymentType: 'drop_in' // Drop-in payments
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Name is required';
    }
    
    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Email is invalid';
    }
    
    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    }

    if (!formData.shippingAddress.trim()) {
      newErrors.shippingAddress = 'Shipping address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentUser) {
      alert('Please log in to complete your purchase.');
      return;
    }


    setLoading(true);
    
    try {
      if (cart.length === 0) {
        throw new Error('Your cart is empty. Add items to checkout.');
      }

      // Support multiple items per order
      const orderData = {
        items: cart.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
          size: item.size || ''
        })),
        shipping_address: formData.shippingAddress,
        payment_type: 'drop_in'
      };

      console.log('Creating merchandise order:', orderData);
      
      // Call backend to create HitPay drop-in payment
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/products/create-qr-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify(orderData)
      });

               const result = await response.json();
               
         
               if (!response.ok) {
                 throw new Error(result.error || 'Failed to create payment');
               }

               if (result.payment_url) {
                 // Redirect to HitPay payment page instead of using drop-in SDK
                 console.log('Redirecting to payment URL:', result.payment_url);
                 
                 // Close the checkout modal
                 onClose();
                 
                 // Use replace to prevent adding HitPay URL to browser history
                 // This helps prevent back button issues after payment
                 window.location.replace(result.payment_url);
               } else {
                 throw new Error('No payment URL received');
               }
      
    } catch (error) {
      console.error('Error creating merchandise order:', error);
      setErrors({ submit: error.message });
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Checkout</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Order Summary */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white mb-6">Order Summary</h3>
            <div className="bg-gray-700 rounded-lg p-4 space-y-4 border border-gray-600">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between items-center">
                  <div>
                    <span className="text-white font-medium">{item.name}</span>
                    <span className="text-gray-400 text-sm ml-2">
                      ({item.size}, {item.gender}) x{item.quantity}
                    </span>
                  </div>
                  <span className="text-primary-400 font-semibold">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="border-t border-gray-600 pt-3 flex justify-between items-center">
                <span className="text-white font-bold text-lg">Total:</span>
                <span className="text-primary-400 font-bold text-lg">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-oldham-gold focus:border-oldham-gold transition-all duration-300 ${
                    errors.customerName ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.customerName && (
                  <p className="text-red-400 text-sm mt-1">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-oldham-gold focus:border-oldham-gold transition-all duration-300 ${
                    errors.customerEmail ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="Enter your email"
                />
                {errors.customerEmail && (
                  <p className="text-red-400 text-sm mt-1">{errors.customerEmail}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-oldham-gold focus:border-oldham-gold transition-all duration-300 ${
                  errors.customerPhone ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter your phone number"
              />
              {errors.customerPhone && (
                <p className="text-red-400 text-sm mt-1">{errors.customerPhone}</p>
              )}
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">
                Shipping Address *
              </label>
              <textarea
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-oldham-gold focus:border-oldham-gold transition-all duration-300 ${
                  errors.shippingAddress ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter your complete shipping address"
              />
              {errors.shippingAddress && (
                <p className="text-red-400 text-sm mt-1">{errors.shippingAddress}</p>
              )}
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">
                Payment Method *
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentType"
                    value="qr"
                    checked={formData.paymentType === 'qr'}
                    onChange={handleInputChange}
                    className="mr-3 text-oldham-gold focus:ring-oldham-gold"
                  />
                  <span className="text-white">QR Code Payment (PayNow / DBS)</span>
                </label>
              </div>
            </div>

              <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
              <p className="text-blue-400 text-sm">
                <strong>Payment:</strong> This uses HitPay for secure payment processing. 
                QR Code payments support PayNow and DBS methods only.
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-oldham-gold hover:bg-oldham-gold/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
           ) : (
             `Proceed to Payment - $${total.toFixed(2)}`
           )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Checkout;